/**
 * Assessment Reports Tab
 *
 * Tab component for viewing and managing assessment reports (beoordelingsverslagen)
 * in the Team Dashboard.
 */

import { useState } from 'react';
import {
  useAssessmentReports,
  useDeleteAssessmentReport,
} from './useAssessmentReports';
import type { SavedAssessmentReport, AssessmentReportStatus } from '../../lib/evaluationsApi';
import { useToast } from '../../components/Toast';

interface AssessmentReportsTabProps {
  employeeId: number;
}

const STATUS_CONFIG: Record<AssessmentReportStatus, { label: string; color: string }> = {
  concept: { label: 'Concept', color: 'bg-yellow-100 text-yellow-800' },
  ingediend: { label: 'Ingediend', color: 'bg-blue-100 text-blue-800' },
  definitief: { label: 'Definitief', color: 'bg-green-100 text-green-800' },
};

export function AssessmentReportsTab({ employeeId }: AssessmentReportsTabProps) {
  const { showToast } = useToast();
  const { data: reportsData, isLoading } = useAssessmentReports(employeeId);
  const deleteReport = useDeleteAssessmentReport();

  const [deleteConfirm, setDeleteConfirm] = useState<SavedAssessmentReport | null>(null);

  const reports = reportsData?.data || [];

  const handleViewReport = (report: SavedAssessmentReport) => {
    // Open report in new tab for viewing/editing
    const url = `/beoordelingsverslag/${report.id}`;
    window.open(url, '_blank');
  };

  const handleDeleteReport = async () => {
    if (!deleteConfirm) return;

    try {
      await deleteReport.mutateAsync({ id: deleteConfirm.id, employeeId });
      setDeleteConfirm(null);
      showToast('Verslag verwijderd', 'success');
    } catch {
      showToast('Fout bij verwijderen', 'error');
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('nl-NL', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const formatDateTime = (dateStr: string) => {
    const d = new Date(dateStr);
    return `${d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })} om ${d.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}`;
  };

  const formatPeriod = (start: string, end: string) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    return `${startDate.toLocaleDateString('nl-NL', { month: 'short', year: 'numeric' })} - ${endDate.toLocaleDateString('nl-NL', { month: 'short', year: 'numeric' })}`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Verslagen laden...</span>
      </div>
    );
  }

  return (
    <div data-testid="assessment-reports-tab">
      {reports.length === 0 ? (
        <div className="text-center py-8">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <p className="mt-2 text-gray-500">
            Nog geen beoordelingsverslagen voor deze medewerker.
          </p>
          <p className="text-sm text-gray-400 mt-1">
            Verslagen worden gemaakt vanuit een beoordeling via "Verslag Genereren".
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {reports.map((report) => {
            const isLocked = report.status === 'definitief';

            return (
              <div
                key={report.id}
                data-testid="assessment-report-item"
                className="bg-white border border-gray-200 rounded-lg p-4 flex items-center justify-between hover:border-gray-300 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-1">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_CONFIG[report.status].color}`}
                    >
                      {isLocked && (
                        <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                      {STATUS_CONFIG[report.status].label}
                    </span>
                    <span className="text-sm text-gray-500">
                      {formatPeriod(report.periodeStart, report.periodeEnd)}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">
                    Aangemaakt op {formatDate(report.createdAt)}
                    {report.createdByName && ` door ${report.createdByName}`}
                  </p>
                  {report.finalizedAt && (
                    <p className="text-xs text-gray-500 mt-0.5">
                      Definitief gemaakt op {formatDateTime(report.finalizedAt)}
                    </p>
                  )}
                </div>

                <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                  {/* View/Edit Button */}
                  <button
                    onClick={() => handleViewReport(report)}
                    className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title={isLocked ? 'Bekijken' : 'Bekijken / Bewerken'}
                  >
                    {isLocked ? (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                      </svg>
                    )}
                  </button>

                  {/* Delete Button (only if not definitief) */}
                  {!isLocked && (
                    <button
                      onClick={() => setDeleteConfirm(report)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Verwijderen"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-gray-900">Verslag verwijderen</h3>
            </div>

            <p className="text-gray-600 mb-2">
              Weet je zeker dat je dit beoordelingsverslag wilt verwijderen?
            </p>
            <p className="text-sm text-gray-500 mb-4">
              Periode: {formatPeriod(deleteConfirm.periodeStart, deleteConfirm.periodeEnd)}
            </p>
            <p className="text-sm text-red-600 mb-6">
              Deze actie kan niet ongedaan worden gemaakt.
            </p>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Annuleren
              </button>
              <button
                onClick={handleDeleteReport}
                disabled={deleteReport.isPending}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {deleteReport.isPending ? 'Verwijderen...' : 'Ja, verwijderen'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
